import React, { useState } from 'react';
import { X, ChevronDown, ChevronUp, Palette, Database, Zap } from 'lucide-react';
// Toggle switch matching the app's design system
const Toggle = ({
  checked,
  onCheckedChange
}: {
  checked: boolean;
  onCheckedChange: (c: boolean) => void;
}) => <button onClick={() => onCheckedChange(!checked)} className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#EC4899] focus:ring-offset-2 focus:ring-offset-slate-900 ${checked ? 'bg-[#EC4899]' : 'bg-slate-700'}`} data-id="element-1079">
    <span className={`${checked ? 'translate-x-5' : 'translate-x-1'} inline-block h-3 w-3 transform rounded-full bg-white transition-transform`} data-id="element-1080" />
  </button>;
export interface ChartSettings {
  chartType: 'line' | 'bar' | 'area' | 'scatter';
  xAxis: 'time';
  yAxisVariable: string;
  yAxisLabel: string;
  showLegend: boolean;
  showGrid: boolean;
  enableZoom: boolean;
  sortByX: boolean;
  // Advanced
  groupModel: boolean;
  groupInitTime: boolean;
  groupZone: boolean;
  quantiles: boolean;
  dynamicRange: boolean;
  unit: 'C' | 'K' | 'F';
  lineStyle: 'solid' | 'dashed' | 'dotted';
  colorMode: 'auto' | 'manual' | 'hierarchical';
  rightAxisEnabled: boolean;
}
export const defaultSettings: ChartSettings = {
  chartType: 'line',
  xAxis: 'time',
  yAxisVariable: 'temp_mean',
  yAxisLabel: 'Auto from variable',
  showLegend: true,
  showGrid: true,
  enableZoom: true,
  sortByX: true,
  groupModel: true,
  groupInitTime: true,
  groupZone: true,
  quantiles: false,
  dynamicRange: true,
  unit: 'C',
  lineStyle: 'solid',
  colorMode: 'auto',
  rightAxisEnabled: false
};
interface ChartSettingsPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  settings: ChartSettings;
  onSettingsChange: (settings: ChartSettings) => void;
  region?: string;
}
// Auto-resolve data source from region + variable
function resolveDataSource(region: string, variable: string): {
  label: string;
  description: string;
} {
  const regionBase = region.replace(/ Wind 100m| Solar| Temperature/g, '').trim();
  const variableLabels: Record<string, string> = {
    temp_mean: 'Temperature Forecast',
    wind_speed: 'Wind & Solar Forecast',
    solar: 'Solar Radiation Forecast'
  };
  const varLabel = variableLabels[variable] || 'Weather Forecast';
  return {
    label: `${regionBase} ${varLabel}`,
    description: `Auto-resolved from ${region} · ${variable === 'temp_mean' ? 'Air Temperature 2m' : variable === 'wind_speed' ? 'Wind Speed 100m' : 'Solar Irradiance'}`
  };
}
export function ChartSettingsPanel({
  open,
  onOpenChange,
  settings,
  onSettingsChange,
  region = 'UK Wind 100m'
}: ChartSettingsPanelProps) {
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [colorConfigOpen, setColorConfigOpen] = useState(true);
  const update = (key: keyof ChartSettings, value: any) => {
    onSettingsChange({
      ...settings,
      [key]: value
    });
  };
  if (!open) return null;
  return <div className="absolute inset-0 z-[50] flex flex-col overflow-hidden bg-[#0B1120]/95 backdrop-blur-sm" data-id="element-1081">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-white/[0.08] px-6 py-4 flex-shrink-0" data-id="element-1082">
        <h2 className="text-lg font-semibold text-white" data-id="element-1083">Display Settings</h2>
        <button onClick={() => onOpenChange(false)} className="rounded-full p-1 text-slate-400 hover:bg-white/[0.06] hover:text-white" data-id="element-1084">
          <X size={20} data-id="element-1085" />
        </button>
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto" data-id="element-1086">
        <div className="space-y-8 p-6" data-id="element-1087">
          {/* Basic Settings */}
          <div className="space-y-6" data-id="element-1088">
            {/* Auto-resolved Data Source */}
            {(() => {
            const source = resolveDataSource(region, settings.yAxisVariable);
            return <div className="space-y-2" data-id="element-1089">
                  <div className="flex items-center gap-2" data-id="element-1090">
                    <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500" data-id="element-1091">
                      Data Source
                    </h3>
                    <span className="flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] font-medium text-emerald-400" data-id="element-1092">
                      <Zap size={8} data-id="element-1093" />
                      auto
                    </span>
                  </div>
                  <div className="flex items-center gap-3 rounded-lg border border-white/[0.06] bg-white/[0.02] px-4 py-3" data-id="element-1094">
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#EC4899]/10" data-id="element-1095">
                      <Database size={14} className="text-[#EC4899]" data-id="element-1096" />
                    </div>
                    <div className="min-w-0 flex-1" data-id="element-1097">
                      <p className="text-sm font-medium text-white truncate" data-id="element-1098">
                        {source.label}
                      </p>
                      <p className="text-[11px] text-slate-500 truncate" data-id="element-1099">
                        {source.description}
                      </p>
                    </div>
                  </div>
                  <p className="text-[11px] text-slate-600 leading-relaxed" data-id="element-1100">
                    Automatically determined by your region and variable
                    selection. Change the region dropdown or Y-axis variable to
                    switch data sources.
                  </p>
                </div>;
          })()}

            <div className="grid grid-cols-2 gap-6" data-id="element-1101">
              <div className="space-y-2" data-id="element-1102">
                <label className="text-xs font-medium text-slate-400" data-id="element-1103">
                  Chart Type
                </label>
                <div className="relative" data-id="element-1104">
                  <select value={settings.chartType} onChange={e => update('chartType', e.target.value)} className="w-full appearance-none rounded-lg border border-white/[0.08] bg-white/[0.04] px-3 py-2 text-sm text-white focus:border-[#EC4899] focus:outline-none" data-id="element-1105">
                    <option value="line" data-id="element-1106">Line</option>
                    <option value="bar" data-id="element-1107">Bar</option>
                    <option value="area" data-id="element-1108">Area</option>
                    <option value="scatter" data-id="element-1109">Scatter</option>
                  </select>
                  <ChevronDown className="absolute right-3 top-2.5 h-4 w-4 text-slate-400 pointer-events-none" data-id="element-1110" />
                </div>
              </div>
              <div className="space-y-2" data-id="element-1111">
                <label className="text-xs font-medium text-slate-400" data-id="element-1112">
                  X-Axis
                </label>
                <div className="relative" data-id="element-1113">
                  <select value={settings.xAxis} disabled className="w-full appearance-none rounded-lg border border-white/[0.08] bg-white/[0.04] px-3 py-2 text-sm text-white opacity-50 cursor-not-allowed" data-id="element-1114">
                    <option value="time" data-id="element-1115">Time</option>
                  </select>
                  <ChevronDown className="absolute right-3 top-2.5 h-4 w-4 text-slate-400 pointer-events-none" data-id="element-1116" />
                </div>
              </div>
            </div>

            <div className="rounded-xl border border-white/[0.08] bg-white/[0.02] p-4" data-id="element-1117">
              <h3 className="mb-4 text-xs font-semibold uppercase tracking-wider text-slate-500" data-id="element-1118">
                Left Y-Axis
              </h3>
              <div className="grid grid-cols-2 gap-6" data-id="element-1119">
                <div className="space-y-2" data-id="element-1120">
                  <label className="text-xs font-medium text-slate-400" data-id="element-1121">
                    Variable
                  </label>
                  <div className="relative" data-id="element-1122">
                    <select value={settings.yAxisVariable} onChange={e => update('yAxisVariable', e.target.value)} className="w-full appearance-none rounded-lg border border-white/[0.08] bg-white/[0.04] px-3 py-2 text-sm text-white focus:border-[#EC4899] focus:outline-none" data-id="element-1123">
                      <option value="temp_mean" data-id="element-1124">
                        Air Temperature 2m (Mean)
                      </option>
                      <option value="wind_speed" data-id="element-1125">Wind Speed 100m</option>
                      <option value="solar" data-id="element-1126">Solar Irradiance</option>
                    </select>
                    <ChevronDown className="absolute right-3 top-2.5 h-4 w-4 text-slate-400 pointer-events-none" data-id="element-1127" />
                  </div>
                </div>
                <div className="space-y-2" data-id="element-1128">
                  <label className="text-xs font-medium text-slate-400" data-id="element-1129">
                    Axis Label
                  </label>
                  <input type="text" value={settings.yAxisLabel} onChange={e => update('yAxisLabel', e.target.value)} className="w-full rounded-lg border border-white/[0.08] bg-white/[0.04] px-3 py-2 text-sm text-white focus:border-[#EC4899] focus:outline-none" data-id="element-1130" />
                </div>
              </div>
            </div>

            <div className="space-y-3" data-id="element-1131">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500" data-id="element-1132">
                Display Options
              </h3>
              <div className="flex flex-wrap gap-6" data-id="element-1133">
                <label className="flex items-center gap-2 cursor-pointer" data-id="element-1134">
                  <Toggle checked={settings.showLegend} onCheckedChange={c => update('showLegend', c)} data-id="element-1135" />
                  <span className="text-sm text-slate-300" data-id="element-1136">Legend</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer" data-id="element-1137">
                  <Toggle checked={settings.showGrid} onCheckedChange={c => update('showGrid', c)} data-id="element-1138" />
                  <span className="text-sm text-slate-300" data-id="element-1139">Grid</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer" data-id="element-1140">
                  <Toggle checked={settings.enableZoom} onCheckedChange={c => update('enableZoom', c)} data-id="element-1141" />
                  <span className="text-sm text-slate-300" data-id="element-1142">Zoom</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer" data-id="element-1143">
                  <Toggle checked={settings.sortByX} onCheckedChange={c => update('sortByX', c)} data-id="element-1144" />
                  <span className="text-sm text-slate-300" data-id="element-1145">Sort by X</span>
                </label>
              </div>
            </div>
          </div>

          {/* Advanced Settings Toggle */}
          <div className="border-t border-white/[0.08] pt-4" data-id="element-1146">
            <button onClick={() => setAdvancedOpen(!advancedOpen)} className="flex items-center gap-2 text-sm font-medium text-[#EC4899] hover:text-[#db2777]" data-id="element-1147">
              {advancedOpen ? <ChevronUp size={16} data-id="element-1148" /> : <ChevronDown size={16} data-id="element-1149" />}
              Advanced Settings
            </button>
          </div>

          {/* Advanced Settings Content */}
          {advancedOpen && <div className="space-y-8 animate-in slide-in-from-top-2 duration-200" data-id="element-1150">
              {/* Group By */}
              <div className="space-y-3" data-id="element-1151">
                <h3 className="text-xs font-medium text-slate-400" data-id="element-1152">
                  Group By (each combination becomes a series)
                </h3>
                <div className="flex gap-4" data-id="element-1153">
                  <label className="flex items-center gap-2 cursor-pointer" data-id="element-1154">
                    <input type="checkbox" checked={settings.groupModel} onChange={e => update('groupModel', e.target.checked)} className="rounded border-white/[0.2] bg-white/[0.04] text-[#EC4899] focus:ring-[#EC4899]" data-id="element-1155" />
                    <span className="text-sm text-slate-300" data-id="element-1156">Model</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer" data-id="element-1157">
                    <input type="checkbox" checked={settings.groupInitTime} onChange={e => update('groupInitTime', e.target.checked)} className="rounded border-white/[0.2] bg-white/[0.04] text-[#EC4899] focus:ring-[#EC4899]" data-id="element-1158" />
                    <span className="text-sm text-slate-300" data-id="element-1159">
                      Initialization Time
                    </span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer" data-id="element-1160">
                    <input type="checkbox" checked={settings.groupZone} onChange={e => update('groupZone', e.target.checked)} className="rounded border-white/[0.2] bg-white/[0.04] text-[#EC4899] focus:ring-[#EC4899]" data-id="element-1161" />
                    <span className="text-sm text-slate-300" data-id="element-1162">Market Zone</span>
                  </label>
                </div>
              </div>

              {/* Filter by Dimension */}
              <div className="space-y-4" data-id="element-1163">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500" data-id="element-1164">
                  Filter by Dimension Values
                </h3>

                {['Model', 'Initialization Time', 'Market Zone'].map(dim => <div key={dim} className="flex items-center gap-4" data-id="element-1165">
                    <span className="w-32 text-sm text-slate-300" data-id="element-1166">{dim}</span>
                    <div className="flex rounded-md bg-white/[0.04] p-0.5" data-id="element-1167">
                      <button className="rounded px-3 py-1 text-xs font-medium bg-[#EC4899] text-white shadow-sm" data-id="element-1168">
                        All
                      </button>
                      <button className="rounded px-3 py-1 text-xs font-medium text-slate-400 hover:text-white" data-id="element-1169">
                        Custom
                      </button>
                    </div>
                  </div>)}
              </div>

              {/* Toggles & Units */}
              <div className="flex flex-wrap items-center gap-8" data-id="element-1170">
                <label className="flex items-center gap-2 cursor-pointer" data-id="element-1171">
                  <Toggle checked={settings.quantiles} onCheckedChange={c => update('quantiles', c)} data-id="element-1172" />
                  <span className="text-sm text-slate-300" data-id="element-1173">Quantiles</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer" data-id="element-1174">
                  <Toggle checked={settings.dynamicRange} onCheckedChange={c => update('dynamicRange', c)} data-id="element-1175" />
                  <span className="text-sm text-slate-300" data-id="element-1176">Dynamic Range</span>
                </label>

                <div className="flex items-center gap-2" data-id="element-1177">
                  <div className="flex rounded-md bg-white/[0.04] p-0.5 border border-white/[0.08]" data-id="element-1178">
                    {(['C', 'K', 'F'] as const).map(u => <button key={u} onClick={() => update('unit', u)} className={`rounded px-3 py-1 text-xs font-medium transition-colors ${settings.unit === u ? 'bg-[#EC4899] text-white' : 'text-slate-400 hover:text-white'}`} data-id="element-1179">
                        °{u}
                      </button>)}
                  </div>
                  <span className="text-xs text-slate-500" data-id="element-1180">(temp. only)</span>
                </div>
              </div>

              {/* Color Configuration */}
              <div className="rounded-xl border border-white/[0.08] bg-white/[0.02] overflow-hidden" data-id="element-1181">
                <button onClick={() => setColorConfigOpen(!colorConfigOpen)} className="flex w-full items-center justify-between px-4 py-3 hover:bg-white/[0.02]" data-id="element-1182">
                  <div className="flex items-center gap-2" data-id="element-1183">
                    <Palette size={16} className="text-slate-400" data-id="element-1184" />
                    <span className="text-sm font-medium text-slate-200" data-id="element-1185">
                      Color Configuration
                    </span>
                  </div>
                  <span className="rounded-full bg-white/[0.06] px-2 py-0.5 text-[10px] font-medium text-slate-400" data-id="element-1186">
                    {settings.colorMode}
                  </span>
                </button>

                {colorConfigOpen && <div className="border-t border-white/[0.08] p-4 space-y-4" data-id="element-1187">
                    <div className="space-y-2" data-id="element-1188">
                      <label className="text-xs font-medium text-slate-400" data-id="element-1189">
                        Line Style
                      </label>
                      <div className="relative" data-id="element-1190">
                        <select value={settings.lineStyle} onChange={e => update('lineStyle', e.target.value)} className="w-full appearance-none rounded-lg border border-white/[0.08] bg-white/[0.04] px-3 py-2 text-sm text-white focus:border-[#EC4899] focus:outline-none" data-id="element-1191">
                          <option value="solid" data-id="element-1192">Solid</option>
                          <option value="dashed" data-id="element-1193">Dashed</option>
                          <option value="dotted" data-id="element-1194">Dotted</option>
                        </select>
                        <ChevronDown className="absolute right-3 top-2.5 h-4 w-4 text-slate-400 pointer-events-none" data-id="element-1195" />
                      </div>
                    </div>

                    <div className="space-y-2" data-id="element-1196">
                      <label className="text-xs font-medium text-slate-400" data-id="element-1197">
                        Color Mode
                      </label>
                      <div className="flex rounded-md bg-white/[0.04] p-0.5 border border-white/[0.08] w-fit" data-id="element-1198">
                        {(['auto', 'manual', 'hierarchical'] as const).map(m => <button key={m} onClick={() => update('colorMode', m)} className={`rounded px-3 py-1.5 text-xs font-medium capitalize transition-colors ${settings.colorMode === m ? 'bg-[#EC4899] text-white' : 'text-slate-400 hover:text-white'}`} data-id="element-1199">
                              {m}
                            </button>)}
                      </div>
                      <p className="text-xs text-slate-500" data-id="element-1200">
                        Automatically cycle through default palette
                      </p>
                    </div>

                    <div className="pt-2" data-id="element-1201">
                      <label className="text-xs font-medium text-slate-400 mb-2 block" data-id="element-1202">
                        Preview
                      </label>
                      <div className="flex items-center gap-2 rounded-md bg-white/[0.04] px-3 py-2 w-fit" data-id="element-1203">
                        <div className="h-3 w-3 rounded-full bg-[#EC4899]" data-id="element-1204" />
                        <span className="text-xs text-slate-300" data-id="element-1205">
                          EPT-2 · Feb 17, 2026
                        </span>
                      </div>
                    </div>
                  </div>}
              </div>

              {/* Right Y-Axis */}
              <div className="flex items-center justify-between border-t border-white/[0.08] pt-4" data-id="element-1206">
                <span className="text-sm font-medium text-slate-200" data-id="element-1207">
                  Enable Right Y-Axis
                </span>
                <Toggle checked={settings.rightAxisEnabled} onCheckedChange={c => update('rightAxisEnabled', c)} data-id="element-1208" />
              </div>
            </div>}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 border-t border-white/[0.08] px-6 py-4 flex-shrink-0" data-id="element-1209">
          <button onClick={() => onOpenChange(false)} className="rounded-lg px-4 py-2 text-sm font-medium text-slate-300 hover:bg-white/[0.06] hover:text-white" data-id="element-1210">
            Cancel
          </button>
          <button onClick={() => onOpenChange(false)} className="rounded-lg bg-[#EC4899] px-4 py-2 text-sm font-medium text-white hover:bg-[#db2777] shadow-lg shadow-pink-900/20" data-id="element-1211">
            Apply Changes
          </button>
        </div>
      </div>
    </div>;
}